import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Table,
  Tag,
  Collapse,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  KeyOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../../services/api';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const ApiKeyIntegracoes = () => {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [result, setResult] = useState(null);
  const [consultLoading, setConsultLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [chave, setChave] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await admin.getApiKeys();
      setApiKeys(Array.isArray(data) ? data : []);
    } catch {
      message.error('Erro ao carregar chaves');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      apiKey: record.apiKey,
      baseUrl: record.baseUrl,
      description: record.description,
      isActive: record.isActive
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await admin.deleteApiKey(id);
      message.success('Chave excluída');
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao excluir');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editing) {
        await admin.updateApiKey(editing.id, values);
        message.success('Chave atualizada');
      } else {
        await admin.createApiKey(values);
        message.success('Chave criada');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const handleConsult = async () => {
    if (!selectedKey) {
      message.warning('Selecione uma chave de API');
      return;
    }
    if (!chave || chave.length !== 44) {
      message.warning('Chave de acesso deve ter 44 dígitos');
      return;
    }
    try {
      setConsultLoading(true);
      setResult(null);
      const data = await admin.queryApiKeyNfe(selectedKey, chave);
      setResult(data);
    } catch (e) {
      message.error(e.message || 'Erro na consulta');
      setResult({ error: e.message });
    } finally {
      setConsultLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    {
      title: 'Base URL',
      dataIndex: 'baseUrl',
      key: 'baseUrl',
      render: (value) => value ? <Text code>{value}</Text> : '-'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'Ativa' : 'Inativa'}</Tag>
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Excluir chave?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Tooltip title="Excluir">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>API KEY - MeuDanfe</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Nova Chave</Button>
        </Space>
      </div>

      <Card title="Consultar NF-e / CT-e (MeuDanfe)" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Select
              placeholder="Selecione a chave de API"
              style={{ width: 300 }}
              value={selectedKey}
              onChange={setSelectedKey}
            >
              {apiKeys.map((key) => (
                <Select.Option key={key.id} value={key.id}>{key.name}</Select.Option>
              ))}
            </Select>
            <Input
              placeholder="Chave de acesso (44 dígitos)"
              maxLength={44}
              style={{ width: 300 }}
              value={chave}
              onChange={(e) => setChave(e.target.value.replace(/\D/g, ''))}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleConsult} loading={consultLoading}>
              Consultar
            </Button>
          </Space>
          {result && (
            <Collapse>
              <Panel header={<Text strong><FileTextOutlined /> Resultado da Consulta</Text>} key="1">
                <pre style={{ maxHeight: 400, overflow: 'auto', background: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12 }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </Panel>
            </Collapse>
          )}
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showTotal: (total) => `Total: ${total}` }}
        />
      </Card>

      <Modal
        title={editing ? 'Editar Chave de API' : 'Nova Chave de API'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
            <Input placeholder="Minha API MeuDanfe" />
          </Form.Item>
          <Form.Item name="apiKey" label="API Key" rules={[{ required: true }]}>
            <Input.Password placeholder="Chave de API" prefix={<KeyOutlined />} />
          </Form.Item>
          <Form.Item name="baseUrl" label="Base URL">
            <Input placeholder="https://api.meudanfe.com.br" />
          </Form.Item>
          <Form.Item name="description" label="Descrição">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isActive" label="Ativo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>Salvar</Button>
              <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ApiKeyIntegracoes;
